import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from '~/hooks/useTranslation'
import { useState } from 'react'

const useServiceRequestForm = ({
  setPage,
  setShowBackButton,
  current_user,
  serviceRequestList,
  serviceRequestType,
}) => {
  const { t } = useTranslation('schedule_modal')

  const [submitted, setSubmitted] = useState(false)

  const handleNext = () => setPage((prevPage) => prevPage + 1)
  const handleBack = () => {
    setPage((prevPage) => prevPage - 1)
    setSubmitted(false)
  }

  const serviceRequest = serviceRequestList[serviceRequestType] || {}

  const message = t('complete_form_tooltip')
  const linkedin_url = z.string().min(1, { message })
  const example_job_1 = z.string()
  const example_job_2 = z.string()
  const location = z.string().min(1, { message })
  const industry = z.string().min(1, { message })

  const schemaMap = {
    'ServiceRequest::LinkedInReview': {
      linkedin_url,
    },
    'ServiceRequest::OrientationCall': {},
    'ServiceRequest::BoardSearch': {},
    'ServiceRequest::ResumeReview': {
      document_id: z.string().min(1, { message }),
      resume_document_id: z.string().min(1, { message }),
      seniority_level: z.string().min(1, { message }),
      example_job_1,
    },
    UploadNewResume: {
      document_id: z.string().min(1, { message }),
      resume_document_id: z.string().min(1, { message }),
    },
    'ResumeRevision::CandidateRevision': {
      notes: z.string().min(1, { message }),
      remote_content: z.instanceof(File).optional().or(z.literal(null)), // Allow null or omitted
    },
    'ServiceRequest::JobSearchStrategy': {},
    'ServiceRequest::InterviewPrep': {},
    'ServiceRequest::JobSearchBurnout': {},
    'ServiceRequest::CareerPivotPlanning': {},
    'ServiceRequest::StrengthsAssessment': {},
    'ServiceRequest::ExecutiveBio': {
      linkedin_url,
      document_id: z.union([z.string(), z.number()]),
      resume_document_id: z.union([z.string(), z.number()]),
    },
    'ServiceRequest::RecruiterTargeting': {
      industry_role: z.string().min(1, { message }),
      location,
      industry,
      salary: z.string().min(1, { message }),
    },
    'ServiceRequest::TargetCompanyResearch': {
      industry,
      public_or_private: z.string().min(1, { message }),
      example_job_1,
      example_job_2,
      revenue_range: z.string().min(1, { message }),
      company_size: z.string().min(1, { message }),
      location,
    },
  }

  const schema = {
    message:
      serviceRequestType !== 'ResumeRevision::CandidateRevision'
        ? z.string().min(1, { message })
        : z.string(),
    ...schemaMap[serviceRequestType],
  }

  const defaultValues = {
    document_id: '',
    message: serviceRequest?.message || '',
    linkedin_url: current_user.candidate_linkedin_url || '',
    resume_document_id: '',
    seniority_level: current_user.candidate_seniority_level || '',
    example_job_1: '',
    example_job_2: '',
    notes: serviceRequest?.notes || '',
    remote_content: null,
    industry_role: '',
    location: '',
    industry: '',
    salary: '',
    public_or_private: '',
    revenue_range: '',
    company_size: '',
  }

  const isValid = (data) => {
    if (
      serviceRequestType === 'ServiceRequest::ResumeReview' ||
      serviceRequestType === 'ServiceRequest::ExecutiveBio'
    ) {
      return data.document_id || data.resume_document_id
    }
    if (serviceRequestType === 'ServiceRequest::TargetCompanyResearch') {
      return data.example_job_1 || data.example_job_2
    }
    return true
  }
  const form = useForm({
    resolver: zodResolver(
      z.object(schema).refine((data) => isValid(data), {
        message,
        path: ['document_id'], // Highlight `document_id` in case of an error
      }),
    ),
    defaultValues,
  })

  const serviceRequestData = Object.keys(schema).reduce((acc, field) => {
    const value = form.getValues(field)
    if (
      field === 'remote_content' ||
      (value !== null && value !== undefined && value !== '')
    )
      acc[field] = form.getValues(field)
    return acc
  }, {})

  const clearForm = () => {
    form.reset(defaultValues)
    setSubmitted(false)
    setShowBackButton(true)
    setPage(1)
  }

  const onSubmit = () => {
    setSubmitted(true)
    handleNext()
  }

  return {
    serviceRequestForm: form,
    onSubmit,
    clearForm,
    submitted,
    handleBack,
    serviceRequestData,
  }
}
export default useServiceRequestForm
